// client/src/pages/seo/MehrsprachigeSpeisekarteDe.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";
import CountryBanner from "../../components/CountryBanner.jsx";

import guest1 from "../../img/seo/important_1.png";
import guest2 from "../../img/seo/important_2.png";
import guest3 from "../../img/seo/important_3.png";
import guest4 from "../../img/seo/important_4.png";
import guest5 from "../../img/seo/important_5.png";

const navItems = [
  {
    id: "bedeutung",
    label: "Was ist eine mehrsprachige Speisekarte für Restaurants?",
  },
  {
    id: "fremdsprachen",
    label: "Fremdsprachige Speisekarten für Restaurants",
  },
  {
    id: "checklist",
    label: "Braucht Ihr Restaurant eine mehrsprachige Speisekarte?",
  },
  {
    id: "wichtig",
    label: "Warum ist eine mehrsprachige Speisekarte wichtig?",
  },
  {
    id: "sicherheit",
    label: "Sprache reduziert Unsicherheit vor der Bestellung",
  },
  {
    id: "sprachen",
    label: "Welche Sprachen sollte ein Restaurant anbieten?",
  },
  {
    id: "automatisch",
    label: "Warum automatische Übersetzung nicht immer ausreicht",
  },
  {
    id: "qr",
    label: "Warum QR-Code-Menüs mehrsprachige Menüs einfacher machen",
  },
  {
    id: "fehler",
    label: "Häufige Fehler bei mehrsprachigen Speisekarten",
  },
  {
    id: "bestplace",
    label: "Wie Best Place Ihnen beim Start einer QR-Code-Speisekarte hilft",
  },
  {
    id: "faq",
    label: "Häufig gestellte Fragen",
  },
];

const acquaintancePhotos = [
  {
    src: guest1,
    alt: "Mehrsprachiges digitales Menü auf einem Smartphone im Restaurant",
  },
  {
    src: guest2,
    alt: "Deutsche Version einer digitalen Speisekarte mit QR-Code",
  },
  {
    src: guest3,
    alt: "Französische Version einer digitalen Restaurant-Speisekarte",
  },
  {
    src: guest4,
    alt: "Italienische Version eines digitalen Menüs im Restaurant",
  },
  {
    src: guest5,
    alt: "Spanische Version einer digitalen Speisekarte mit QR-Code",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const checklist = [
  "Ihr Restaurant bedient regelmäßig internationale Gäste.",
  "Touristen fragen oft nach Englisch oder einer anderen Sprache.",
  "Ihre Speisekarte enthält regionale Gerichte, die schwer zu erklären sind.",
  "Gäste fragen häufig nach Zutaten, Allergenen oder Zubereitung.",
  "Sie möchten Missverständnisse bei der Bestellung reduzieren.",
  "Ihre Speisekarte soll online und über QR-Code verständlicher sein.",
  "Ihr Restaurant befindet sich in einer touristischen Stadt oder in der Nähe von Hotels.",
  "Sie möchten mehrere Sprachen anbieten, ohne mehrere gedruckte Speisekarten zu verwenden.",
  "Ihr Restaurant soll auf internationale Gäste professioneller wirken.",
];

const faqLinks = [
  {
    question: "Muss ein Gast eine App installieren, um ein QR-Menü zu öffnen?",
    answer:
      "Nein. Ein gutes QR-Menü sollte direkt im Browser des Smartphones geöffnet werden. Das macht die Nutzung einfach und nimmt Gästen eine der größten Hürden.",
    linkText: "Mehr über QR-Code-Speisekarten ohne App lesen",
    to: "/de/qr-code-speisekarte-ohne-app",
  },
  {
    question: "Wie kann ein Restaurant seine Speisekarte am besten übersetzen?",
    answer:
      "Das hängt davon ab, wie oft sich die Speisekarte ändert und wie viele internationale Gäste das Restaurant bedient. Gedruckte Menüs können bei stabilen Speisekarten reichen. KI kann beim ersten Entwurf helfen. Ein mehrsprachiges QR-Menü ist meist flexibler, wenn mehrere Sprachen und regelmäßige Aktualisierungen nötig sind.",
    linkText: "Möglichkeiten zur Übersetzung von Speisekarten vergleichen",
    to: "/de/speisekarte-ubersetzen-restaurant",
  },
  {
    question: "Kann ich eine digitale Speisekarte kostenlos testen, bevor ich mich entscheide?",
    answer:
      "Ja. Für viele Restaurants ist der sinnvollste erste Schritt kein großes technisches Projekt, sondern ein einfacher Test: Speisekarte erstellen, QR-Code scannen und prüfen, wie alles auf einem echten Smartphone wirkt.",
    linkText: "Mehr über den kostenlosen Test einer digitalen Speisekarte lesen",
    to: "/de/digitale-speisekarte-kostenlos-testen",
  },
  {
    question: "Kann ich meine Speisekarte online aktualisieren, ohne sie neu zu drucken?",
    answer:
      "Ja. Genau das ist einer der praktischsten Gründe für eine digitale Speisekarte. Preise, nicht verfügbare Gerichte, Tagesangebote und Fotos können online geändert werden, ohne eine PDF-Datei neu zu erstellen oder neue Karten zu drucken.",
    linkText: "Mehr über die Online-Verwaltung einer Speisekarte lesen",
    to: "/de/speisekarte-online-verwalten",
  },
  {
    question: "Sind Bilder in einer digitalen Speisekarte wirklich wichtig?",
    answer:
      "Sie können sehr wichtig sein, besonders bei Gerichten, Getränken, Menüs, Desserts und saisonalen Angeboten. Ein Gast versteht ein Angebot oft schneller, wenn es klar gezeigt wird und nicht nur beschrieben ist.",
    linkText: "Mehr über Speisekarten mit Bildern lesen",
    to: "/de/speisekarte-mit-bildern",
  },
  {
    question: "Kann ich einen QR-Code auf Tischaufstellern verwenden?",
    answer:
      "Ja. Tischaufsteller sind einer der natürlichsten Orte für ein QR-Menü, weil der Gast den Code genau dann sieht, wenn er etwas auswählen möchte.",
    linkText: "Mehr über QR-Code-Tischaufsteller lesen",
    to: "/de/restaurant-qr-code-tischaufsteller",
  },
  {
    question: "Kann ein QR-Menü mit Online-Tischreservierung zusammenarbeiten?",
    answer:
      "Ja. Eine digitale Speisekarte kann Teil eines größeren Restaurant-Systems sein: Speisekarte, Tischreservierung, Fotos, aktuelle Angebote und später auch Bestellabläufe.",
    linkText: "Mehr über Online-Tischreservierung lesen",
    to: "/de/online-tischreservierung",
  },
  {
    question: "Ist eine digitale Speisekarte auch für eine Pizzeria sinnvoll?",
    answer:
      "Ja, besonders wenn Sie Größen, Beläge, Extras, Menüs, Mittagsangebote oder saisonale Aktionen übersichtlich zeigen möchten.",
    linkText: "Mehr über digitale Speisekarten für Pizzerien lesen",
    to: "/de/digitale-speisekarte-pizzeria",
  },
];

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Eine mehrsprachige Speisekarte ist vielleicht noch keine Priorität",
      text:
        "Wenn Ihre Gäste fast ausschließlich lokal sind und Ihre Speisekarte sehr einfach ist, kann eine einsprachige Speisekarte im Moment ausreichen.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Eine zweite Sprache ist einen Test wert",
      text:
        "Ihr Restaurant könnte davon profitieren, eine weitere Sprache hinzuzufügen, besonders wenn Gäste häufig nach Gerichten, Zutaten oder Preisen fragen.",
      tone: "medium",
    };
  }

  return {
    title: "Eine mehrsprachige Speisekarte kann sehr sinnvoll sein",
    text:
      "Mehrere Punkte sprechen dafür, dass Ihr Restaurant von einer mehrsprachigen Speisekarte profitieren kann: Touristen, klarere Bestellungen, weniger Rückfragen und ein professionellerer Eindruck bei internationalen Gästen.",
    tone: "high",
  };
}

export default function MehrsprachigeSpeisekarteDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/multilingual-menu-for-restaurants",
          de: "/de/mehrsprachige-speisekarte",
          no: "/no/flerspraklig-meny-for-restauranter",
        }}
      />

      <section id="ready" className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Mehrsprachige Speisekarte für Restaurants: leichter verstehen,
              leichter bestellen
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p>
                Eine mehrsprachige Speisekarte ist mehr als nur eine Übersetzung.
                Sie hilft Gästen, Gerichte sicherer zu verstehen, reduziert
                Rückfragen und macht die Bestellung einfacher.
              </p>

              <p>
                Für internationale Gäste, Touristen oder bei regionalen
                Spezialitäten kann Sprache entscheidend dafür sein, ob ein Gast
                selbstbewusst etwas Neues ausprobiert oder lieber nur das wählt,
                was er bereits kennt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="countryBannerBlock">
        <CountryBanner
          reverse
          ariaLabel={"Countries and languages"}
        />
      </div>

      <SeoContactSection
        lang="de"
        apiOrigin={CALLBACK_API_ORIGIN}
        title="Brauchen Sie Hilfe beim Erstellen einer mehrsprachigen Speisekarte?"
      />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {navItems.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollTo(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="bedeutung" className="acquaintance">
          <h2>Was ist eine mehrsprachige Speisekarte für Restaurants?</h2>

          <p>
            Eine mehrsprachige Speisekarte zeigt Namen von Gerichten,
            Beschreibungen, Kategorien und wichtige Informationen in mehreren
            Sprachen. In vielen Fällen bleibt die lokale Sprache die
            Hauptversion, während Englisch oder eine andere Sprache als
            zusätzliche Version angeboten wird.
          </p>

          <p>
            Technisch kann das sehr einfach funktionieren: Der Gast scannt einen
            QR-Code und öffnet eine Web-App mit der Speisekarte. Die passende
            Sprache kann direkt im Menü ausgewählt werden. Genau darin liegt ein
            großer Vorteil gegenüber klassischen gedruckten Speisekarten, denn
            diese Flexibilität ist praktisch nur digital möglich.
          </p>

          <p>
            In der nächsten Sektion können Sie mit einem kurzen Check prüfen, ob
            diese Art von digitalem Menü für Ihr Restaurant sinnvoll ist.
          </p>
        </section>

        <section id="fremdsprachen" className="acquaintance">
          <h2>
            Fremdsprachige Speisekarten für Restaurants: einfacher internationale
            Gäste bedienen
          </h2>

          <p>
            Fremdsprachige Speisekarten sind oft schon wichtig, bevor ein Gast
            überhaupt am Tisch sitzt. Viele Touristen und internationale Gäste
            informieren sich zuerst online über ein Restaurant. Wenn sie sehen,
            dass die Speisekarte in einer Sprache verfügbar ist, die sie
            verstehen, wirkt das Restaurant einfacher, sicherer und einladender.
            Für manche Gäste kann genau das beeinflussen, welches Restaurant sie
            auswählen.
          </p>

          <p>
            Deshalb sollten Speisekarten in Fremdsprachen für Restaurantbesitzer
            nicht kompliziert sein. Sie müssen keine separaten PDF-Dateien
            erstellen, keine gedruckten Karten neu gestalten und nicht dieselbe
            Speisekarte immer wieder manuell aufbauen. Mit einer digitalen
            mehrsprachigen Speisekarte kann der Prozess viel einfacher sein: Das
            Restaurant bereitet eine Excel-Datei mit Gerichten, Beschreibungen,
            Preisen, Kategorien und den benötigten Sprachversionen vor. Nach dem
            Hochladen kann daraus eine fertige QR-Code-Speisekarte mit den
            Sprachen entstehen, die Ihre Gäste brauchen.
          </p>

          <p>
            So behält das Restaurant eine klare Menüstruktur und bietet mehrere
            Sprachversionen innerhalb derselben Speisekarte an. Gäste öffnen das
            Menü auf dem Smartphone, wählen ihre Sprache, verstehen Gerichte
            schneller und bestellen mit mehr Sicherheit. Für das Team bedeutet
            das weniger Übersetzungsfragen, einfachere Aktualisierungen und ein
            besseres Erlebnis für ausländische Gäste ohne zusätzliche gedruckte
            Speisekarten.
          </p>
        </section>

        <section id="checklist" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Braucht Ihr Restaurant eine mehrsprachige Speisekarte?</h2>

            <p>
              Dieser kurze Check hilft Ihnen einzuschätzen, ob eine zweite oder
              dritte Sprache für Ihr Restaurant sinnvoll sein kann.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />

                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="wichtig" className="acquaintance">
          <h2>Warum ist eine mehrsprachige Speisekarte wichtig?</h2>

          <p>
            In touristischen Städten und internationalen Geschäftsregionen können
            ausländische Gäste einen erheblichen Teil der Nachfrage in der
            Gastronomie ausmachen. Die Deutsche Zentrale für Tourismus berichtete
            zum Beispiel von 85,3 Millionen Übernachtungen ausländischer Gäste in
            Deutschland im Jahr 2024. In manchen Stadtregionen ist der Anteil
            noch deutlicher sichtbar: Eine Studie von DEHOGA / IW Consult nannte
            für Düsseldorf einen Anteil von 41,2 % der Gästeübernachtungen durch
            ausländische Gäste. Für Restaurants bedeutet das: Internationale
            Besucher sind nicht unbedingt eine seltene Ausnahme. Je nach Standort
            können sie ein normaler Teil des täglichen Geschäfts sein.
          </p>

          <div
            className="picture__slider"
            aria-label="Vorteile einer mehrsprachigen Speisekarte im Restaurant"
          >
            {acquaintancePhotos.map((photo, index) => (
              <div className="picture__slide" key={index}>
                <img src={photo.src} alt={photo.alt} />
              </div>
            ))}
          </div>

          <p>
            Wenn diese Gäste die Speisekarte verstehen, sinkt die Unsicherheit
            vor der Bestellung. Sie müssen weniger raten, können regionale
            Gerichte besser einschätzen und fühlen sich schneller willkommen.
          </p>

          <p>
            Digitale Lösungen können genau hier helfen: Sie machen Informationen
            schneller verfügbar, erleichtern Aktualisierungen und geben Gästen
            mehr Klarheit, bevor sie bestellen. Eine mehrsprachige Speisekarte
            unterstützt also nicht nur die Kommunikation, sondern auch das
            gesamte Service-Erlebnis.
          </p>
        </section>

        <section id="sicherheit" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Sprache reduziert Unsicherheit vor der Bestellung</h2>

            <p>
              Wenn Gäste ein Gericht nicht verstehen, bestellen sie oft
              vorsichtiger. Sie wählen etwas Bekanntes, stellen mehrere Fragen
              oder verzichten auf Extras. Eine klare Speisekarte kann diese
              Unsicherheit reduzieren.
            </p>

            <div className="multiMenuArticle__comparison">
              <div>
                <h3>Nur eine Sprache</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Gäste müssen raten oder nachfragen
                  </li>
                  <li className="text__seo-li">
                    regionale Begriffe bleiben unklar
                  </li>
                  <li className="text__seo-li">
                    Allergene und Zutaten wirken unsicher
                  </li>
                  <li className="text__seo-li">
                    Extras oder Beilagen werden leichter übersehen
                  </li>
                </ul>
              </div>

              <div className="multiMenuArticle__comparisonHighlight">
                <h3>Mehrsprachige Speisekarte</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    mehr Sicherheit bei der Bestellung
                  </li>
                  <li className="text__seo-li">
                    weniger Rückfragen an das Personal
                  </li>
                  <li className="text__seo-li">
                    bessere Erklärung lokaler Spezialitäten
                  </li>
                  <li className="text__seo-li">
                    professionellerer Eindruck bei internationalen Gästen
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Das Ziel ist nicht, jeden Menütext lang zu machen. Das Ziel ist,
              genug Klarheit zu geben, damit der Gast sich bei seiner Auswahl
              sicher fühlt.
            </p>
          </div>
        </section>

        <section id="sprachen" className="acquaintance">
          <h2>Welche Sprachen sollte ein Restaurant anbieten?</h2>

          <p>
            Für viele Restaurants ist der beste Start einfach: die lokale Sprache
            und Englisch. Danach hängen die richtigen zusätzlichen Sprachen vom
            Standort, der Zielgruppe und dem tatsächlichen Gästeprofil ab.
          </p>

          <div className="multiMenuArticle__cardGrid">
            <div>
              <strong>Englisch</strong>
              <p>oft die wichtigste erste Zusatzsprache</p>
            </div>

            <div>
              <strong>Französisch</strong>
              <p>sinnvoll in Regionen mit französischsprachigen Gästen</p>
            </div>

            <div>
              <strong>Italienisch</strong>
              <p>interessant für touristische Orte oder italienische Küche</p>
            </div>

            <div>
              <strong>Spanisch</strong>
              <p>hilfreich in internationalen Städten und touristischen Regionen</p>
            </div>

            <div>
              <strong>Niederländisch</strong>
              <p>relevant in Regionen mit vielen Gästen aus den Niederlanden</p>
            </div>

            <div>
              <strong>Polnisch</strong>
              <p>sinnvoll je nach Region, Publikum oder Standort</p>
            </div>
          </div>

          <h3>Europa bleibt sprachlich vielfältig</h3>

          <p>
            Die Europäische Kommission berichtet, dass 59&nbsp;% der Europäer
            sich in mindestens einer Fremdsprache unterhalten können. Gleichzeitig
            zeigt diese Zahl auch: Nicht jeder Gast versteht jede Fremdsprache
            gut genug, um eine Speisekarte bequem zu lesen.
          </p>
        </section>

        <section id="automatisch" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum automatische Übersetzung nicht immer ausreicht</h2>

            <p>
              Automatische Übersetzung und KI-Dienste können beim Übersetzen von
              Speisekarten in Fremdsprachen helfen. Trotzdem sind sie nicht immer
              ausreichend, wenn ein Restaurant eine verlässliche und klare
              Speisekarte für internationale Gäste anbieten möchte.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                gleiche Begriffe können unterschiedlich übersetzt werden
              </li>
              <li className="text__seo-li">
                regionale Gerichte können missverständlich wirken
              </li>
              <li className="text__seo-li">
                Allergene und wichtige Zutaten brauchen besondere Kontrolle
              </li>
              <li className="text__seo-li">
                die Qualität der Gerichtebeschreibungen muss überprüfbar bleiben
              </li>
            </ul>

            <p>
              Speisekarten sind sensibel: regionale Gerichte, Zubereitungsarten,
              Allergene, Fleischsorten, Beilagen und kulturelle Begriffe können
              schnell unklar oder missverständlich werden.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Gerichtenamen prüfen</li>
              <li className="text__seo-li">Allergene sorgfältig übersetzen</li>
              <li className="text__seo-li">
                wichtige Zutaten nicht unklar lassen
              </li>
              <li className="text__seo-li">
                Schärfegrad und Zubereitung erklären
              </li>
              <li className="text__seo-li">
                Portionen, Beilagen und Extras verständlich machen
              </li>
              <li className="text__seo-li">
                Preise und Optionen klar darstellen
              </li>
            </ul>

            <p>
              Eine gute Menüübersetzung muss nicht poetisch sein. Sie muss dem
              Gast helfen, die richtige Entscheidung zu treffen.
            </p>
          </div>
        </section>

        <section id="qr" className="acquaintance">
          <h2>Warum QR-Code-Menüs mehrsprachige Menüs einfacher machen</h2>

          <p>
            Bei gedruckten Speisekarten bedeutet jede zusätzliche Sprache oft
            mehr Seiten, mehr Layoutarbeit, mehr Druckkosten und mehr Pflege.
            Ein digitales QR-Code-Menü kann den Umgang mit mehreren Sprachen
            deutlich flexibler machen.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">QR-Code scannen</li>
            <li className="text__seo-li">Speisekarte öffnen</li>
            <li className="text__seo-li">Sprache auswählen</li>
            <li className="text__seo-li">Gerichte verstehen</li>
            <li className="text__seo-li">mit mehr Sicherheit bestellen</li>
          </ul>

          <p>
            Wenn Gäste ihre Sprache selbst auswählen können, muss das Team nicht
            jedes Detail am Tisch übersetzen. Das Personal kann sich stärker auf
            Service, Empfehlungen und Gastfreundschaft konzentrieren.
          </p>
        </section>

        <section id="fehler" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Häufige Fehler bei mehrsprachigen Speisekarten</h2>

            <div className="multiMenuArticle__mistakes">
              <div>
                <h3>1. Nur Gerichtenamen übersetzen</h3>
                <p>
                  Der Name allein reicht oft nicht aus. Gäste brauchen manchmal
                  auch Informationen zu Zutaten, Zubereitung oder Beilagen.
                </p>
              </div>

              <div>
                <h3>2. Allergene ungenau übersetzen</h3>
                <p>
                  Allergene und wichtige Hinweise brauchen in jeder Sprache
                  besondere Sorgfalt.
                </p>
              </div>

              <div>
                <h3>3. Zu viele Sprachen auf Papier</h3>
                <p>
                  Gedruckte Speisekarten können schnell unübersichtlich werden,
                  wenn jede Sprache auf jeder Seite erscheint.
                </p>
              </div>

              <div>
                <h3>4. Übersetzungen nicht prüfen</h3>
                <p>
                  Automatische Übersetzung kann helfen, aber Menütexte sollten
                  trotzdem kontrolliert werden.
                </p>
              </div>

              <div>
                <h3>5. Sprachwechsel ist schwer zu finden</h3>
                <p>
                  Wenn Gäste die Sprachoption nicht finden, hilft der
                  mehrsprachige Inhalt nur wenig.
                </p>
              </div>

              <div>
                <h3>6. Preise und Optionen sind unklar</h3>
                <p>
                  Beilagen, Extras und zusätzliche Preise müssen in jeder Sprache
                  verständlich sein.
                </p>
              </div>
            </div>
          </div>
        </section>

        <SeoWorkSteps lang="de" />


        <section id="faq" className="articls">
          <h2>Häufig gestellte Fragen</h2>

          <div className="qrArticle__faq">
            {faqLinks.map((item) => (
              <details key={item.to}>
                <summary>{item.question}</summary>

                <p>{item.answer}</p>

                <p>
                  <Link to={item.to}>{item.linkText}</Link>
                </p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </main>
  );
}